import React, { useContext } from 'react';
import { Check, Info } from 'lucide-react';
import { LanguageContext } from '../context/LanguageContext';
import { Section, SectionHeader } from './ui/Section';
import { Button } from './ui/Button';

const CheckList = ({ title, items }) => (
  <div>
    <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-brand-teal">{title}</h3>
    <ul className="space-y-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
          <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-teal" aria-hidden="true" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const FreeSoftware = () => {
  const languageContext = useContext(LanguageContext);
  if (!languageContext) return null;

  const { freeSoftware = {}, support = {}, language } = languageContext;
  const prefix = language === 'es' ? '/es' : '';
  const pick = (list, nums, fallback) => nums.map((n) => list?.[n] || `${fallback} ${n}`);

  return (
    <Section tone="mist" id="free-software">
      <SectionHeader
        eyebrow={freeSoftware.tagline || 'Sin inversión inicial'}
        title={freeSoftware.title || 'Sin hardware que comprar, sin software que instalar'}
        lead={
          freeSoftware.description ||
          'Moderniza tu lavandería sin comprar dispositivos ni software. Solo paga una suscripción por máquina y recibe todo lo necesario: hardware, software y soporte incluidos.'
        }
      />

      <div className="mt-12 grid overflow-hidden rounded-[2rem] bg-white lg:grid-cols-12">
        {/* Pricing */}
        <div className="flex flex-col justify-between bg-brand-navy p-8 text-white md:p-12 lg:col-span-5">
          <div>
            <p className="mb-6 text-sm font-semibold text-brand-mint">
              {freeSoftware.subscriptionTitle || 'Suscripción simple por máquina'}
            </p>
            <p className="mb-1 text-sm text-white/60">{freeSoftware.pricingFrom || 'Desde solo'}</p>
            <p className="mb-0 flex items-baseline gap-2 text-white">
              <span className="font-display text-6xl font-extrabold tracking-tight">${freeSoftware.price || '6.99'}</span>
              <span className="text-white/60">{freeSoftware.perMachine || '/máquina/mes'}*</span>
            </p>
            <p className="mb-0 mt-3 text-sm text-white/70">{freeSoftware.pricingNote || 'Todo incluido, sin compras de hardware'}</p>
            <p className="mb-0 mt-1 text-xs text-white/50">
              * {freeSoftware.pricingAsterisk || 'Tan bajo como $6.99 dependiendo del volumen de máquinas'}
            </p>
          </div>

          <div className="mt-10">
            <Button to={`${prefix}/subscription-plans`} variant="inverse" size="lg">
              {freeSoftware.ctaButton || 'Ver planes de suscripción'}
            </Button>
            <p className="mb-0 mt-3 text-sm text-white/60">
              {freeSoftware.ctaSubtext || 'Planes flexibles adaptados a tus necesidades. Sin compromiso a largo plazo.'}
            </p>
          </div>
        </div>

        {/* What's included */}
        <div className="p-8 md:p-12 lg:col-span-7">
          <div className="grid gap-10 sm:grid-cols-2">
            <CheckList
              title={freeSoftware.hardwareTitle || 'Hardware incluido'}
              items={pick(freeSoftware.hardwareFeatures, [1, 2, 3], 'Dispositivo')}
            />
            <CheckList
              title={freeSoftware.softwareTitle || 'Software incluido'}
              items={pick(freeSoftware.softwareFeatures, [1, 2, 3], 'Software')}
            />
          </div>

          <div className="mt-10 border-t border-brand-line pt-10">
            <CheckList
              title={freeSoftware.benefitsTitle || 'Beneficios adicionales'}
              items={pick(freeSoftware.benefits, [1, 2, 3, 4], 'Beneficio')}
            />
          </div>

          <div className="mt-10 flex items-start gap-3 rounded-2xl bg-brand-mist p-5">
            <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-teal" aria-hidden="true" />
            <div>
              <p className="mb-1 text-sm font-semibold text-brand-navy">
                {freeSoftware.installationTitle || 'Cargo único de instalación'}
              </p>
              <p className="mb-0 text-sm text-slate-600">
                {freeSoftware.installationNote ||
                  'La instalación tiene un cargo único que cubre la configuración de todas tus máquinas. Después de la instalación inicial, solo pagas la suscripción mensual.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <p className="mb-0 mt-6 text-center text-sm text-slate-500">
        {support.contact || '¿Tienes preguntas? Escríbenos a support@washpayapp.com'}
      </p>
    </Section>
  );
};

export default FreeSoftware;
